import Vue from 'vue';
import Vuex from 'vuex';
import mbcService from '@/services/mbcService';
import ssoService from '@/services/ssoService';
import pmtService from '@/services/pmtService';
import sctService from '@/services/sctService';
import actService from '@/services/actService';
import comSVC from '@/services/comService';
import redeemItem from './redeemItem';
import memberSource from './memberSource';
import badge from './badge';

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    redeemItem,
    memberSource,
    badge,
  },
  state: {
    isMobile: false,
    themeColor: '',
    layoutType: '',
    companyId: '',
    accessToken: '',
    memberSourceId: '',
    memberId: '',
    memberType: null,
    memberBonus: null,
    memberData: null,
    memberDataWithoutPromise: null,
    memberCenterMenu: null,
    memberTypeSetting: null,
    memberIndicatior: null,
    companyPermission: null,
    clientNavigationMenu: null,
    memberSourceSetting: null,
    currentOrderTab: '',
    currentEticketTab: '',
    electronicCommerce: null,
    mgmActivityData: null,
    rGBaCode: '',
  },
  mutations: {
    set(state, { name, value }) {
      state[name] = value;
    },
    push(state, { name, value }) {
      state[name].push(value);
    },
    delete(state, { name }) {
      state[name] = null;
    },
    // 清除會員的資料
    deleteMemberState(state) {
      state.memberType = null;
      state.memberBonus = null;
      state.memberData = null;
      state.redeemItem.redeemTime = null;
    },
  },
  actions: {
    // 取得會籍資料
    getMemberType: ({ state, commit }) => {
      // 若state中沒有會籍資料的promise，呼叫API
      if (!state.memberType) {
        const body = { companyId: state.companyId };
        const headers = { accessToken: state.accessToken };
        const memberType = ssoService.getDigest(state.companyId, state.memberSourceId, body)
          .toPromise()
          .then((res) => {
            headers.digest = res.response.data[0].digest;
            return mbcService.getMemberType(body, headers).toPromise();
          })
          .then((res) => ({
            currentLevelId: res.response.data[0].memberTypeID,
            currentLevel: res.response.data[0].memberTypeName,
            nextLevel: res.response.data[0].upgMemberTypeName,
            currentAmount: res.response.data[0].totalAmount,
            upgradeAmount: res.response.data[0].upgAmount,
            cardPic: res.response.data[0].cardPic,
            iconPic: res.response.data[0].iconPic,
            color: res.response.data[0].color,
            barcodeSource: res.response.data[0].barcodeSource,
            qrcodeSource: res.response.data[0].qrcodeSource,
          }));
        commit('set', { name: 'memberType', value: memberType });
      }

      // 若state中已有會籍資料的promise，直接回傳
      return state.memberType;
    },
    // 取得會員紅利資料
    getMemberBonus: ({ state, commit }) => {
      // 若state中沒有會員紅利資料的promise，呼叫API
      if (!state.memberBonus) {
        const body = { companyId: state.companyId, memberSourceId: state.memberSourceId, memberId: state.memberId };
        const headers = {};
        const memberBonus = ssoService.getDigest(state.companyId, state.memberSourceId, body)
          .toPromise()
          .then((res) => {
            headers.digest = res.response.data[0].digest;
            return pmtService.getMemberBonus(body, headers).toPromise();
          })
          .then((res) => ({
            pointSum: res.response.data[0].pointSum,
            point: res.response.data[0].point,
            summary: res.response.data[0].pointSummary,
          }));
        commit('set', { name: 'memberBonus', value: memberBonus });
      }

      // 若state中已有會員紅利資料的promise，直接回傳
      return state.memberBonus;
    },
    // 取得會員資料
    getMemberData: ({ state, commit }) => {
      // 若state中沒有會員資料的promise，呼叫API
      if (!state.memberData) {
        const body = { companyId: state.companyId };
        const headers = { accessToken: state.accessToken };
        const memberData = ssoService.getDigest(state.companyId, state.memberSourceId, body)
          .toPromise()
          .then((res) => {
            headers.digest = res.response.data[0].digest;
            return mbcService.getCustomerData(body, headers).toPromise().then((res2) => {
              commit('set', { name: 'memberDataWithoutPromise', value: res2.response.data[0] });

              return res2;
            });
          })
          .then((res) => res.response.data[0]);
        commit('set', { name: 'memberData', value: memberData });
      }

      // 若state中已有會員資料的promise，直接回傳
      return state.memberData;
    },
    // 取得會員中心功能選單
    getMemberCenterMenu: ({ state, commit }) => {
      // 若state中沒有會員中心功能選單的promise，呼叫API
      if (!state.memberCenterMenu) {
        const param = { companyId: state.companyId, memberSourceId: state.memberSourceId };
        const memberCenterMenu = ssoService.getMemberCentreMenu(param)
          .toPromise()
          .then((res) => res.response.data);
        commit('set', { name: 'memberCenterMenu', value: memberCenterMenu });
      }

      // 若state中已有會員中心功能選單的promise，直接回傳
      return state.memberCenterMenu;
    },
    // 取得會員訂單資料
    getMemberOrder: ({ state }, payload) => {
      const body = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        memberId: state.memberId,
        // 目前固定取六個月資料
        dayfilter: 180,
        source: payload.source,
      };

      // 分頁資料，起始筆數、結束筆數
      if (payload.pagination) {
        body.startLoadingCnt = payload.pagination.offset + 1;
        body.endLoadingCnt = payload.pagination.offset + payload.pagination.limit;
      }

      // 篩選條件 - 訂單狀態
      if (payload.status) body.status = payload.status;

      return ssoService.getDigest(state.companyId, state.memberSourceId, body)
        .toPromise()
        .then((res) => {
          const headers = { digest: res.response.data[0].digest };
          return sctService.getOrders(body, headers).toPromise();
        });
    },
    // 取得會員訂單數量
    getMemberOrderCount: ({ state }, source) => {
      const body = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        memberId: state.memberId,
        // 目前固定取六個月資料
        dayfilter: 180,
        source,
      };

      return ssoService.getDigest(state.companyId, state.memberSourceId, body)
        .toPromise()
        .then((res) => {
          const headers = { digest: res.response.data[0].digest };
          return sctService.getOrderCount(body, headers).toPromise();
        });
    },
    // 取頁面設定資料
    getPageSetting: ({ state, commit }, page) => {
      // 若state中沒有頁面設定資料的promise，呼叫API
      if (!state[`pageSetting${page}`]) {
        const body = { companyId: state.companyId, memberSourceId: state.memberSourceId, page };
        const pageSetting = ssoService.getPageSetting(body)
          .toPromise()
          .then((res) => {
            commit('set', { name: 'rGBaCode', value: res.response.data[0].rGBaCode });
            return res.response.data[0];
          });
        commit('set', { name: `pageSetting${page}`, value: pageSetting });
      }

      // 設定頁面色系
      state[`pageSetting${page}`].then((setting) => {
        commit('set', { name: 'themeColor', value: { '--theme-color': setting.rGBaCode } });
        commit('set', { name: 'layoutType', value: setting.layoutType === '0' ? 'mcf-dark' : 'mcf-light' });
      });

      // 若state中已有頁面設定資料的promise，直接回傳
      return state[`pageSetting${page}`];
    },
    // 取得所有會籍設定
    getMemberTypeSetting: ({ state, commit }) => {
      // 若state中沒有所有會籍設定的promise，呼叫API
      if (!state.memberTypeSetting) {
        const param = { companyId: state.companyId };
        const memberTypeSetting = mbcService.getMemberTypeSetting(param)
          .toPromise()
          .then((res) => res.response.data);
        commit('set', { name: 'memberTypeSetting', value: memberTypeSetting });
      }

      // 若state中已有所有會籍設定的promise，直接回傳
      return state.memberTypeSetting;
    },
    // 取得公司資訊
    getCompanyPermission: ({ state, commit }) => {
      // 若state中沒有公司資訊promise，呼叫API
      if (!state.companyPermission) {
        const param = { companyId: state.companyId };
        const companyPermission = ssoService.getCompanyPermission(param)
          .toPromise()
          .then((res) => res.response.data[0]);
        commit('set', { name: 'companyPermission', value: companyPermission });
      }

      // 若state中已有公司資訊的promise，直接回傳
      return state.companyPermission;
    },
    // 取得會員關鍵指標
    getMemberIndicator: ({ state, commit }) => {
      // 若state中沒有會員關鍵指標promise，呼叫API
      if (!state.memberIndicatior) {
        const memberIndicatior = mbcService.getIndicator({ accessToken: state.accessToken })
          .toPromise()
          .then((res) => res.response.data);
        commit('set', { name: 'memberIndicatior', value: memberIndicatior });
      }
      // 若state中已有會員關鍵指標的promise，直接回傳
      return state.memberIndicatior;
    },
    // 取得客戶端導覽列設定
    getClientNavigationMenu: ({ state, commit }) => {
      // 若state中沒有客戶端導覽列設定的promise，呼叫API
      if (!state.clientNavigationMenu) {
        const param = { companyId: state.companyId, memberSourceId: state.memberSourceId };
        const clientNavigationMenu = ssoService.getClientNavigationMenu(param)
          .toPromise()
          .then((res) => res.response.data);
        commit('set', { name: 'clientNavigationMenu', value: clientNavigationMenu });
      }

      // 若state中已有客戶端導覽列設定的promise，直接回傳
      return state.clientNavigationMenu;
    },
    // 取得會員來源設定資料
    getMemberSourceSetting: ({ state, commit }) => {
      // 若state中沒有會員來源設定資料promise，呼叫API
      if (!state.memberSourceSetting) {
        const param = { companyId: state.companyId, memberSourceId: state.memberSourceId };
        const memberSourceSetting = ssoService.getMemberSourceSetting(param)
          .toPromise()
          .then((res) => res.response.data[0]);
        commit('set', { name: 'memberSourceSetting', value: memberSourceSetting });
      }

      // 若state中已有會員來源設定資料的promise，直接回傳
      return state.memberSourceSetting;
    },
    // 取得電商資料
    getElectronicCommerce({ state, commit }) {
      // 若state中沒有電商資料 promise，呼叫API
      if (!state.electronicCommerce) {
        const electronicCommerce = ssoService.getElectronicCommerce({ companyId: state.companyId })
          .toPromise()
          .then((res) => res.response.data[0]);
        commit('set', { name: 'electronicCommerce', value: electronicCommerce });
      }

      // 若state中已有電商資料 promise，直接回傳
      return state.electronicCommerce;
    },
    getMGMInfo({ state }) {
      const params = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
      };

      return actService.getMGMInfo(params)
        .toPromise()
        .then((res) => res.response)
        .catch((err) => err);
    },
    getMGMUserinfo({ commit, state }, payload = {}) {
      const params = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        activityId: payload.activityId,
        memberId: state.memberId,
      };

      return actService.getMGMUserinfo(params)
        .toPromise()
        .then((res) => res.response)
        .catch((err) => err)
        .then((res) => {
          if (res.success) {
            commit('set', { name: 'mgmActivityData', value: res.data[0] });
          }

          return res;
        });
    },
    // 取得退貨申請列表
    getReturnRequests: ({ state }, payload) => {
      const body = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        memberId: state.memberId,
        // 目前固定取六個月資料
        dayfilter: 180,
      };

      // 分頁資料，起始筆數、結束筆數
      if (payload.pagination) {
        body.startLoadingCnt = payload.pagination.offset + 1;
        body.endLoadingCnt = payload.pagination.offset + payload.pagination.limit;
      }

      return sctService.getReturnRequests(body).toPromise();
    },
    postMgmInvitedButton({ state }, payload) {
      const body = {
        companyId: state.companyId,
        memberSourceId: payload.memberSourceId,
        buttonType: payload.buttontype,
        memberId: payload.memberId,
        activityId: payload.activityID,
        activityStatus: payload.activityStatus,
      };

      return actService.postMgmInvitedButton(body)
        .toPromise()
        .then((res) => res.response)
        .catch((err) => err);
    },
    getOfficalAccount(_, companyid) {
      const params = {
        companyID: companyid,
      };
      return comSVC.getChannel(params)
        .toPromise()
        .then((res) => res.response)
        .catch((err) => err);
    },
    // 確認會員資料是否存在
    checkMemberExistence: ({ state }, payload) => {
      const body = {
        companyId: state.companyId,
        type: 0,
        categoryId: 'Mobile',
        identityIds: [payload.mobile],
      };

      return mbcService.checkMemberExistence(body).toPromise();
    },
    postMgmInvitedPage({ state }, payload) {
      const body = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        pageType: 0,
        memberId: state.memberId,
        activityId: payload.activityID,
        activityStatus: payload.activityStatus,
      };

      return actService.postMgmInvitedPage(body)
        .toPromise()
        .then((res) => res.response)
        .catch((err) => err);
    },
    // 取得商店主檔列表
    getStores({ state }) {
      return sctService.getStores({ companyId: state.companyId })
        .toPromise()
        .then((res) => res.response.data);
    },
    getEticketList: ({ state }, payload) => {
      const body = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        memberId: state.memberId,
        status: payload.status === 'usable' ? 0 : 1,
        dayFilter: 92,
      };

      // 分頁資料，起始筆數、結束筆數
      if (payload.pagination) {
        body.offset = payload.pagination.offset + 1;
        body.limit = payload.pagination.limit;
      }

      return ssoService.getDigest(state.companyId, state.memberSourceId, body)
        .toPromise()
        .then((res) => {
          const headers = { digest: res.response.data[0].digest };
          return pmtService.getEticketList(body, headers).toPromise();
        });
    },
    getEticketListCount: ({ state }) => {
      const body = {
        companyId: state.companyId,
        memberSourceId: state.memberSourceId,
        memberId: state.memberId,
        dayFilter: 92,
      };

      return ssoService.getDigest(state.companyId, state.memberSourceId, body)
        .toPromise()
        .then((res) => {
          const headers = { digest: res.response.data[0].digest };
          return pmtService.getEticketListCount(body, headers).toPromise();
        });
    },
  },
});
